"use client"

import { memo, useLayoutEffect, useRef } from "react"
import { X } from "lucide-react"
import { AnimatePresence, LayoutGroup, motion } from "motion/react"
import { cn } from "@/lib/utils"
import { ContextItem, TYPE_ICONS } from "./chat-context-selector"

const TAG_MOTION_TRANSITION = {
  layout: { type: "spring" as const, stiffness: 300, damping: 34 },
  opacity: { duration: 0.15 },
  scale: { duration: 0.15 },
}

export interface ChatContextTagsProps {
  items: ContextItem[]
  onRemove: (id: string) => void
  /** Isolate layout projection from other Motion layout groups */
  layoutGroupId?: string
}

export const ChatContextTags = memo(function ChatContextTags({
  items,
  onRemove,
  layoutGroupId = "chat-context-tags",
}: ChatContextTagsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevIdsRef = useRef<Set<string>>(new Set())
  const hasCommittedRef = useRef(false)

  useLayoutEffect(() => {
    if (!hasCommittedRef.current) {
      hasCommittedRef.current = true
      prevIdsRef.current = new Set(items.map((i) => i.id))
      return
    }

    const prev = prevIdsRef.current
    const added = items.filter((item) => !prev.has(item.id))
    prevIdsRef.current = new Set(items.map((i) => i.id))

    if (added.length === 0) {
      return
    }

    const targetId = added[added.length - 1].id
    requestAnimationFrame(() => {
      const root = scrollRef.current
      if (!root) {
        return
      }
      const el = root.querySelector<HTMLElement>(
        `[data-context-tag-id="${CSS.escape(targetId)}"]`,
      )
      el?.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      })
    })
  }, [items])

  return (
    <LayoutGroup id={layoutGroupId}>
      <div className={cn("min-w-0 w-full", items.length > 0 && "mb-2")}>
        <div
          ref={scrollRef}
          className={cn(
            "scroll-mask-x flex min-w-0 w-full flex-nowrap gap-1.5 overflow-y-hidden pb-0.5",
            "[--scroll-mask-size:1.25rem]"
          )}
        >
        <AnimatePresence mode="popLayout">
          {items.map((item) => {
            const Icon = TYPE_ICONS[item.type]
            return (
              <motion.span
                key={item.id}
                data-context-tag-id={item.id}
                layout
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={TAG_MOTION_TRANSITION}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 pl-2 pr-1 py-1.5 text-xs rounded-full border shadow-sm",
                  item.type === "page"
                    ? "border-transparent text-white"
                    : "bg-white dark:bg-transparent border-border",
                    {
                      "font-semibold": item.type == "page"
                    }
                )}
                style={item.type === "page" ? { background: "var(--gradient-chat-action)" } : undefined}
              >
                <Icon className={cn("size-3 shrink-0", item.type === "page" ? "text-white/80" : "text-muted-foreground")} />
                <span className="truncate max-w-28">{item.label}</span>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className={cn(
                    "rounded-full p-0.5 transition-colors shrink-0",
                    item.type === "page" ? "hover:bg-white/20" : "hover:bg-foreground/10"
                  )}
                >
                  <X className="size-3" />
                </button>
              </motion.span>
            )
          })}
        </AnimatePresence>
        </div>
      </div>
    </LayoutGroup>
  )
})
